import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { loadDiscoverEditorial } from '../TrendingPage/DiscoverEditorial.duck';

const BOOK_LISTING_TYPE = 'book-listing';
const ISO_LISTING_TYPE = 'iso-book';
const BOOKS_LIMIT = 12;
const ISO_LIMIT = 4;
const FRESHNESS_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export const isWithinDiscoverFreshnessWindow = (createdAt, now = new Date()) => {
  const created = new Date(createdAt).getTime();
  const current = now.getTime();
  return Number.isFinite(created) && created <= current && created >= current - FRESHNESS_WINDOW_MS;
};

const isPublishedType = (listing, listingType) =>
  !listing.attributes?.deleted &&
  listing.attributes?.state === 'published' &&
  listing.attributes?.publicData?.listingType === listingType;

const listingQuery = (config, listingType, perPage) => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};

  return {
    pub_listingType: listingType,
    states: ['published'],
    page: 1,
    perPage,
    // Sharetribe sorts createdAt descending by default; '-createdAt' would return oldest first.
    sort: 'createdAt',
    include: ['author', 'author.profileImage', 'images'],
    'fields.listing': [
      'title',
      'createdAt',
      'deleted',
      'state',
      'price',
      'publicData.listingType',
      'publicData.author',
      'publicData.bookAuthor',
      'publicData.edition',
      'publicData.editionsource',
      'publicData.budget',
      'publicData.maxBudget',
      'publicData.condition',
      'publicData.conditionPreference',
      'publicData.signedpreference',
      'publicData.notes',
    ],
    'fields.user': [
      'banned',
      'deleted',
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData.shopName',
      'profile.publicData.province',
    ],
    'fields.image': [
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    ...createImageVariantConfig(variantPrefix, 400, aspectHeight / aspectWidth),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectHeight / aspectWidth),
    'limit.images': 1,
  };
};

const fetchListingSection = async (sdk, dispatch, config, listingType, perPage, now) => {
  try {
    const response = await sdk.listings.query(listingQuery(config, listingType, perPage));
    dispatch(addMarketplaceEntities(response));
    return {
      listings: denormalisedResponseEntities(response).filter(
        listing =>
          isPublishedType(listing, listingType) &&
          isWithinDiscoverFreshnessWindow(listing.attributes?.createdAt, now)
      ),
      error: null,
    };
  } catch (error) {
    return { listings: [], error: storableError(error) };
  }
};

const loadDiscoverPayloadCreator = ({ config, now = new Date() }, { dispatch, extra: sdk }) =>
  Promise.all([
    fetchListingSection(sdk, dispatch, config, BOOK_LISTING_TYPE, BOOKS_LIMIT, now),
    fetchListingSection(sdk, dispatch, config, ISO_LISTING_TYPE, ISO_LIMIT, now),
  ]).then(([books, isoRequests]) => ({ books, isoRequests }));

export const loadDiscover = createAsyncThunk(
  'DiscoverLandingPage/loadDiscover',
  loadDiscoverPayloadCreator
);

/** Distinct shops in listing-query order, not a sales ranking. Omit known unavailable sellers. */
export const shopsFromListings = listings => {
  const seen = new Set();
  return listings.reduce((shops, listing) => {
    const seller = listing.author;
    const sellerId = seller?.id?.uuid;
    const attributes = seller?.attributes || {};
    if (
      !sellerId ||
      seen.has(sellerId) ||
      attributes.banned ||
      attributes.deleted ||
      (attributes.state && attributes.state !== 'active')
    )
      return shops;
    seen.add(sellerId);
    return [...shops, seller];
  }, []);
};

const initialState = {
  books: [],
  isoRequests: [],
  booksLoading: false,
  isoLoading: false,
  booksError: null,
  isoError: null,
  loadError: null,
};

const discoverLandingPageSlice = createSlice({
  name: 'DiscoverLandingPage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDiscover.pending, state => {
        state.booksLoading = true;
        state.isoLoading = true;
        state.booksError = null;
        state.isoError = null;
        state.loadError = null;
      })
      .addCase(loadDiscover.fulfilled, (state, action) => {
        state.books = action.payload.books.listings;
        state.isoRequests = action.payload.isoRequests.listings;
        state.booksLoading = false;
        state.isoLoading = false;
        state.booksError = action.payload.books.error;
        state.isoError = action.payload.isoRequests.error;
      })
      .addCase(loadDiscover.rejected, (state, action) => {
        state.booksLoading = false;
        state.isoLoading = false;
        state.loadError = action.payload || action.error;
      });
  },
});

export const loadData = (_params, _search, config) => dispatch =>
  Promise.all([dispatch(loadDiscover({ config })), dispatch(loadDiscoverEditorial())]);

export default discoverLandingPageSlice.reducer;
